import { useState, useEffect } from "react";
import { apiFetch } from "../../../api";
import s from "./MonitorPanel.module.css";

function timeAgo(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
}

function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
}

// Canonical module order matching LearningBoard
const MODULE_ORDER = [
  { id: "story_recap",       title: "Story",                    step: 1 },
  { id: "deep_practice_en",  title: "Listen & Respond (EN Guide)", step: 2 },
  { id: "speaking_practice", title: "Flashcard",                  step: 3 },
  { id: "deep_practice_sv",  title: "Listen & Respond (SV Only)", step: 4 },
  { id: "roleplay",          title: "Role Play",                step: 5 },
];

function FaceSvg({ type, size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="14" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeWidth="1.5" />
      {type === "boring" && (
        <>
          <line x1="10" y1="13" x2="13" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="19" y1="13" x2="22" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="11" y1="20" x2="21" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </>
      )}
      {type === "good" && (
        <>
          <circle cx="11.5" cy="13" r="1.3" fill="currentColor" />
          <circle cx="20.5" cy="13" r="1.3" fill="currentColor" />
          <path d="M11 18.5 Q16 23 21 18.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        </>
      )}
      {type === "excited" && (
        <>
          <path d="M10 13.5 Q11.5 11.5 13 13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          <path d="M19 13.5 Q20.5 11.5 22 13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          <path d="M10 18 Q16 25.5 22 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        </>
      )}
    </svg>
  );
}

const FEELING_MAP = {
  1: { type: "boring",  label: "Boring",   color: "#908070" },
  2: { type: "good",    label: "Good",     color: "#2a6e3e" },
  3: { type: "excited", label: "Exciting", color: "#a07820" },
};

function SortIcon({ column, sortBy, sortDir }) {
  const active = sortBy === column;
  return (
    <span className={`${s.sortIcon} ${active ? s.sortIconActive : ""}`}>
      {active ? (sortDir === "asc" ? "▲" : "▼") : "▲"}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "completed"
      ? s.statusCompleted
      : status === "in_progress"
      ? s.statusInProgress
      : s.statusNotStarted;
  const label =
    status === "completed"
      ? "Completed"
      : status === "in_progress"
      ? "In Progress"
      : "Not Started";
  return <span className={`${s.statusBadge} ${cls}`}>{label}</span>;
}

// ── User Detail View ──────────────────────────────────────────────────────────

function UserDetail({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await apiFetch(`/api/v1/admin/hub/users/${userId}`);
        if (!res.ok) throw new Error("Failed to load user details");
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) return <div className={s.state}>Loading user details…</div>;
  if (error) return <div className={s.stateError}>{error}</div>;
  if (!data) return null;

  const { user, dashboard } = data;

  return (
    <div className={s.detailWrap}>
      <button className={s.backBtn} onClick={onBack}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        All Users
      </button>

      <div className={s.detailHeader}>
        <div className={s.detailAvatar}>{user.email[0].toUpperCase()}</div>
        <div>
          <div className={s.detailEmail}>{user.email}</div>
          <div className={s.detailMeta}>
            <span className={`${s.roleBadge} ${user.role === "admin" ? s.roleAdmin : s.roleUser}`}>
              {user.role}
            </span>
            <span className={s.detailMetaText}>Joined {timeAgo(user.created_at)}</span>
          </div>
        </div>
      </div>

      {!dashboard ? (
        <div className={s.detailEmpty}>No dashboard yet — user has not logged in.</div>
      ) : (
        <>
          <div className={s.detailSection}>
            <div className={s.detailSectionLabel}>Dashboard</div>
            <div className={s.detailStat}>
              <span className={s.detailStatLabel}>Last login</span>
              <span className={s.detailStatValue}>{formatDate(dashboard.last_login)}</span>
            </div>
            <div className={s.detailStat}>
              <span className={s.detailStatLabel}>Stories assigned</span>
              <span className={s.detailStatValue}>{dashboard.stories.length}</span>
            </div>
          </div>

          {dashboard.stories.length === 0 ? (
            <div className={s.detailEmpty}>No stories on this dashboard yet.</div>
          ) : (
            <div className={s.storiesList}>
              <div className={s.detailSectionLabel}>Stories</div>
              {dashboard.stories.map((story) => (
                <div key={story.story_id} className={s.storyCard}>
                  <div className={s.storyCardHeader}>
                    <div className={s.storyTopic}>
                      <div className={s.storyTopicSv}>{story.topic_sv || "—"}</div>
                      <div className={s.storyTopicEn}>{story.topic_en || "—"}</div>
                    </div>
                    <StatusBadge status={story.status} />
                  </div>

                  <div className={s.storyTiming}>
                    <div className={s.timingItem}>
                      <span className={s.timingLabel}>Started</span>
                      <span className={s.timingValue}>{formatDate(story.started_at)}</span>
                    </div>
                    <div className={s.timingItem}>
                      <span className={s.timingLabel}>Completed</span>
                      <span className={s.timingValue}>{formatDate(story.completed_at)}</span>
                    </div>
                    <div className={s.timingItem}>
                      <span className={s.timingLabel}>Duration</span>
                      <span className={s.timingValue}>{formatDuration(story.duration_seconds)}</span>
                    </div>
                  </div>

                  <div className={s.modulesTableWrap}>
                    <div className={s.modulesLabel}>Modules</div>
                    <table className={s.modulesTable}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Module</th>
                          <th>Done</th>
                          <th>Started</th>
                          <th>Completed</th>
                          <th>Duration</th>
                          <th>Feeling</th>
                        </tr>
                      </thead>
                      <tbody>
                        {MODULE_ORDER.map((def) => {
                          const mod = story.modules.find((m) => m.module_id === def.id);
                          const done = mod?.has_user_completed ?? false;
                          const started = mod?.started_at ?? null;
                          const completed = mod?.completed_at ?? null;
                          const feeling = mod?.feeling ?? null;
                          const dur = started && completed
                            ? formatDuration(Math.floor((new Date(completed) - new Date(started)) / 1000))
                            : null;
                          const feelingInfo = feeling != null ? FEELING_MAP[feeling] : null;
                          return (
                            <tr key={def.id} className={done ? s.moduleRowDone : s.moduleRowPending}>
                              <td className={s.moduleStep}>{def.step}</td>
                              <td className={s.moduleTitle}>{def.title}</td>
                              <td className={s.moduleCheck}>
                                {done ? (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2a6e3e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <span className={s.moduleDash}>—</span>
                                )}
                              </td>
                              <td className={s.moduleDate}>{started ? formatDate(started) : <span className={s.moduleDash}>—</span>}</td>
                              <td className={s.moduleDate}>{completed ? formatDate(completed) : <span className={s.moduleDash}>—</span>}</td>
                              <td className={s.moduleDuration}>{dur ?? <span className={s.moduleDash}>—</span>}</td>
                              <td className={s.moduleFeeling}>
                                {feelingInfo ? (
                                  <span className={s.feelingWrap} style={{ color: feelingInfo.color }} title={feelingInfo.label}>
                                    <FaceSvg type={feelingInfo.type} size={20} />
                                    <span className={s.feelingLabel}>{feelingInfo.label}</span>
                                  </span>
                                ) : (
                                  <span className={s.moduleDash}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Monitor Tab ───────────────────────────────────────────────────────────────

function MonitorTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await apiFetch("/api/v1/admin/hub/monitor-stats");
        if (!res.ok) throw new Error("Failed to load stats");
        const data = await res.json();
        if (!cancelled) setStats(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className={s.state}>Loading stats…</div>;
  if (error) return <div className={s.stateError}>{error}</div>;
  if (!stats) return null;

  const totalFeelings = stats.feelings.boring + stats.feelings.good + stats.feelings.exciting;

  return (
    <>
      {/* User Growth */}
      <div className={s.monitorSection}>
        <div className={s.monitorSectionTitle}>User Growth</div>
        <div className={s.statsGrid}>
          <div className={s.statCard}>
            <div className={s.statCardTitle}>Total Users</div>
            <div className={s.statCardValue}>{stats.users.total}</div>
            <div className={s.statCardSub}>
              <span className={s.statCardAccent}>{stats.users.premium}</span> premium · <span className={s.statCardAccent}>{stats.users.verified}</span> verified
            </div>
          </div>
          <div className={s.statCard}>
            <div className={s.statCardTitle}>Joined Today</div>
            <div className={s.statCardValue}>{stats.users.joined_today}</div>
          </div>
          <div className={s.statCard}>
            <div className={s.statCardTitle}>Last 7 Days</div>
            <div className={s.statCardValue}>{stats.users.joined_7d}</div>
          </div>
          <div className={s.statCard}>
            <div className={s.statCardTitle}>Last 30 Days</div>
            <div className={s.statCardValue}>{stats.users.joined_30d}</div>
          </div>
        </div>
      </div>

      {/* Activity & Learning */}
      <div className={s.monitorSection}>
        <div className={s.monitorSectionTitle}>Activity & Learning</div>
        <div className={s.statsGrid}>
          <div className={s.statCard}>
            <div className={s.statCardTitle}>Active Users (7d)</div>
            <div className={s.statCardValue}>{stats.users.active_7d}</div>
            <div className={s.statCardSub}>Logged in within 7 days</div>
          </div>
          <div className={s.statCard}>
            <div className={s.statCardTitle}>Pending Support</div>
            <div className={s.statCardValue}>{stats.support.pending}</div>
            <div className={s.statCardSub}>Unanswered tickets</div>
          </div>
          {stats.waiting_list && stats.waiting_list.total > 0 && (
            <div className={s.statCard}>
              <div className={s.statCardTitle}>Waiting List</div>
              <div className={s.statCardValue}>{stats.waiting_list.total}</div>
              <div className={s.statCardSub}>Users waiting for Premium</div>
            </div>
          )}
        </div>
      </div>

      {/* Module Feelings */}
      {totalFeelings > 0 && (
        <div className={s.monitorSection}>
          <div className={s.monitorSectionTitle}>Module Feedback ({totalFeelings} ratings)</div>
          <div className={s.feelingsRow}>
            <div className={s.feelingCard}>
              <FaceSvg type="boring" size={28} />
              <div>
                <div className={s.feelingCount} style={{ color: "#908070" }}>{stats.feelings.boring}</div>
                <div className={s.feelingName} style={{ color: "#908070" }}>Boring</div>
              </div>
            </div>
            <div className={s.feelingCard}>
              <FaceSvg type="good" size={28} />
              <div>
                <div className={s.feelingCount} style={{ color: "#2a6e3e" }}>{stats.feelings.good}</div>
                <div className={s.feelingName} style={{ color: "#2a6e3e" }}>Good</div>
              </div>
            </div>
            <div className={s.feelingCard}>
              <FaceSvg type="excited" size={28} />
              <div>
                <div className={s.feelingCount} style={{ color: "#a07820" }}>{stats.feelings.exciting}</div>
                <div className={s.feelingName} style={{ color: "#a07820" }}>Exciting</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Create User Modal ─────────────────────────────────────────────────────────

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (email !== confirmEmail) { setError("Emails do not match."); return; }
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/v1/auth/sign-up", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || "Failed to create user.");
      onCreated();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={s.backdrop} onClick={onClose}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <h3 className={s.modalTitle}>Create User</h3>
          <button className={s.modalClose} onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form className={s.modalForm} onSubmit={handleSubmit}>
          <label className={s.fieldLabel}>Email</label>
          <input
            className={s.fieldInput}
            type="email"
            required
            autoFocus
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label className={s.fieldLabel}>Confirm Email</label>
          <input
            className={s.fieldInput}
            type="email"
            required
            placeholder="Repeat email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
          />

          {error && <p className={s.modalError}>{error}</p>}

          <div className={s.modalFooter}>
            <button type="button" className={s.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" className={s.submitBtn} disabled={submitting}>
              {submitting ? "Creating…" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [sortBy, setSortBy] = useState("joined");
  const [sortDir, setSortDir] = useState("desc");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/v1/admin/hub/users");
      if (!res.ok) throw new Error("Failed to load users");
      const data = await res.json();
      setUsers(data.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDeleteUser(userId) {
    setDeleting(userId);
    setDeleteError("");
    try {
      const res = await apiFetch(`/api/v1/admin/hub/users/${userId}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to delete user.");
      }
      setDeleteTarget(null);
      setDeleteInput("");
      load();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(null);
    }
  }

  function handleSort(col) {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
  }

  const sortedUsers = [...users].sort((a, b) => {
    let cmp = 0;
    if (sortBy === "joined") {
      cmp = new Date(a.created_at) - new Date(b.created_at);
    } else if (sortBy === "last_login") {
      const aVal = a.last_login ? new Date(a.last_login).getTime() : 0;
      const bVal = b.last_login ? new Date(b.last_login).getTime() : 0;
      cmp = aVal - bVal;
    } else if (sortBy === "plan") {
      cmp = (a.is_premium ? 1 : 0) - (b.is_premium ? 1 : 0);
    } else if (sortBy === "completed") {
      cmp = (a.completed_count || 0) - (b.completed_count || 0);
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  if (selectedUserId) {
    return (
      <UserDetail
        userId={selectedUserId}
        onBack={() => setSelectedUserId(null)}
      />
    );
  }

  return (
    <>
      <div className={s.usersToolbar}>
        <span className={s.usersCount}>{loading ? "—" : `${users.length} user${users.length === 1 ? "" : "s"}`}</span>
        <button className={s.createBtn} onClick={() => setShowModal(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Create User
        </button>
      </div>

      {loading ? (
        <div className={s.state}>Loading users…</div>
      ) : error ? (
        <div className={s.stateError}>{error}</div>
      ) : !users.length ? (
        <div className={s.state}>No users yet.</div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Verified</th>
                <th>Role</th>
                <th className={s.sortableHeader} onClick={() => handleSort("plan")}>
                  Plan <SortIcon column="plan" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th className={s.sortableHeader} onClick={() => handleSort("joined")}>
                  Joined <SortIcon column="joined" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th className={s.sortableHeader} onClick={() => handleSort("last_login")}>
                  Last Login <SortIcon column="last_login" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th className={s.sortableHeader} onClick={() => handleSort("completed")}>
                  Completed <SortIcon column="completed" sortBy={sortBy} sortDir={sortDir} />
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => (
                <tr
                  key={user.id}
                  className={s.clickableRow}
                  onClick={() => setSelectedUserId(user.id)}
                >
                  <td className={s.emailCell}>{user.email}</td>
                  <td className={s.numCell}>
                    {user.email_verified ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2a6e3e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className={s.moduleDash}>—</span>
                    )}
                  </td>
                  <td>
                    <span className={`${s.roleBadge} ${user.role === "admin" ? s.roleAdmin : s.roleUser}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`${s.planBadge} ${user.is_premium ? s.planPremium : s.planFree}`}>
                      {user.is_premium ? "Premium" : "Free"}
                    </span>
                  </td>
                  <td className={s.dateCell}>{timeAgo(user.created_at)}</td>
                  <td className={s.dateCell}>{user.last_login ? timeAgo(user.last_login) : "Never"}</td>
                  <td className={s.numCell}>{user.completed_count || 0}</td>
                  <td>
                    {user.role !== "admin" && (
                      <button
                        className={s.revokeBtn}
                        disabled={deleting === user.id}
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(user); setDeleteInput(""); }}
                      >
                        {deleting === user.id ? "…" : "Delete"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <CreateUserModal
          onClose={() => setShowModal(false)}
          onCreated={load}
        />
      )}

      {deleteTarget && (
        <div className={s.backdrop} onClick={() => { setDeleteTarget(null); setDeleteError(""); }}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <h3 className={s.modalTitle}>Delete User</h3>
              <button className={s.modalClose} onClick={() => { setDeleteTarget(null); setDeleteError(""); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className={s.modalForm}>
              <p className={s.revokeWarning}>
                This will <strong>permanently delete</strong> the user <strong>{deleteTarget.email}</strong> and all their data (progress, subscriptions, reviews, conversations, support messages). This action cannot be undone.
              </p>
              <label className={s.fieldLabel}>Type the user's email to confirm</label>
              <input
                className={s.fieldInput}
                type="text"
                placeholder={deleteTarget.email}
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                autoFocus
              />
              {deleteError && <p className={s.modalError}>{deleteError}</p>}
            </div>
            <div className={s.modalFooter}>
              <button className={s.cancelBtn} onClick={() => { setDeleteTarget(null); setDeleteError(""); }}>
                Cancel
              </button>
              <button
                className={s.revokeBtn}
                disabled={deleteInput !== deleteTarget.email || deleting === deleteTarget.id}
                onClick={() => handleDeleteUser(deleteTarget.id)}
              >
                {deleting === deleteTarget.id ? "…" : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Grant Premium Modal ──────────────────────────────────────────────────────

function GrantPremiumModal({ onClose, onGranted, users }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [duration, setDuration] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedUserId) { setError("Please select a user."); return; }
    setError("");
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/v1/admin/subscriptions/grant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: selectedUserId, duration_days: duration }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || "Failed to grant premium.");
      onGranted();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={s.backdrop} onClick={onClose}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <h3 className={s.modalTitle}>Grant Premium</h3>
          <button className={s.modalClose} onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form className={s.modalForm} onSubmit={handleSubmit}>
          <label className={s.fieldLabel}>User</label>
          <select
            className={s.fieldInput}
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            required
          >
            <option value="">Select a user…</option>
            {users
              .filter((u) => u.role !== "admin")
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}{u.is_premium ? " (Premium)" : ""}
                </option>
              ))}
          </select>

          <label className={s.fieldLabel}>Duration</label>
          <select
            className={s.fieldInput}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
          >
            <option value={7}>7 days (1 week)</option>
            <option value={14}>14 days (2 weeks)</option>
            <option value={30}>30 days (1 month)</option>
            <option value={60}>60 days (2 months)</option>
            <option value={90}>90 days (3 months)</option>
            <option value={180}>180 days (6 months)</option>
            <option value={365}>365 days (1 year)</option>
          </select>

          {error && <p className={s.modalError}>{error}</p>}

          <div className={s.modalFooter}>
            <button type="button" className={s.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" className={s.submitBtn} disabled={submitting}>
              {submitting ? "Granting…" : "Grant Premium"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Subscriptions Tab ────────────────────────────────────────────────────────

function SubscriptionsTab() {
  const [subs, setSubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeInput, setRevokeInput] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [subsRes, usersRes] = await Promise.all([
        apiFetch("/api/v1/admin/subscriptions"),
        apiFetch("/api/v1/admin/hub/users"),
      ]);
      if (!subsRes.ok) throw new Error("Failed to load subscriptions");
      if (!usersRes.ok) throw new Error("Failed to load users");
      const subsData = await subsRes.json();
      const usersData = await usersRes.json();
      setSubs(subsData.subscriptions);
      setUsers(usersData.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRevoke(userId) {
    setRevoking(userId);
    try {
      const res = await apiFetch("/api/v1/admin/subscriptions/revoke", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!res.ok) throw new Error("Failed to revoke");
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRevoking(null);
    }
  }

  return (
    <>
      <div className={s.usersToolbar}>
        <span className={s.usersCount}>
          {loading ? "—" : `${subs.length} subscription${subs.length === 1 ? "" : "s"}`}
        </span>
        <button className={s.createBtn} onClick={() => setShowModal(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Grant Premium
        </button>
      </div>

      {loading ? (
        <div className={s.state}>Loading subscriptions…</div>
      ) : error ? (
        <div className={s.stateError}>{error}</div>
      ) : !subs.length ? (
        <div className={s.state}>No subscriptions yet.</div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Granted By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((sub) => (
                <tr key={sub.id} className={sub.is_active ? "" : s.rowExpired}>
                  <td className={s.emailCell}>{sub.user_email}</td>
                  <td>
                    <span className={`${s.planBadge} ${sub.is_active ? s.planPremium : s.planExpired}`}>
                      {sub.is_active ? "Active" : "Expired"}
                    </span>
                  </td>
                  <td className={s.dateCell}>{formatDate(sub.premium_until)}</td>
                  <td className={s.dateCell}>{sub.granted_by_email || "—"}</td>
                  <td>
                    <button
                      className={s.revokeBtn}
                      disabled={revoking === sub.user_id}
                      onClick={() => { setRevokeTarget(sub); setRevokeInput(""); }}
                    >
                      {revoking === sub.user_id ? "…" : "Revoke"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revokeTarget && (
        <div className={s.backdrop} onClick={() => setRevokeTarget(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <h3 className={s.modalTitle}>Confirm Revoke</h3>
              <button className={s.modalClose} onClick={() => setRevokeTarget(null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className={s.modalForm}>
              <p className={s.revokeWarning}>
                To revoke the subscription for <strong>{revokeTarget.user_email}</strong>, type their email below to confirm.
              </p>
              <label className={s.fieldLabel}>Email</label>
              <input
                className={s.fieldInput}
                type="text"
                placeholder={revokeTarget.user_email}
                value={revokeInput}
                onChange={(e) => setRevokeInput(e.target.value)}
                autoFocus
              />
            </div>
            <div className={s.modalFooter}>
              <button className={s.cancelBtn} onClick={() => setRevokeTarget(null)}>
                Cancel
              </button>
              <button
                className={s.revokeBtn}
                disabled={revokeInput !== revokeTarget.user_email || revoking === revokeTarget.user_id}
                onClick={() => {
                  handleRevoke(revokeTarget.user_id);
                  setRevokeTarget(null);
                }}
              >
                {revoking === revokeTarget.user_id ? "…" : "Revoke"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <GrantPremiumModal
          onClose={() => setShowModal(false)}
          onGranted={load}
          users={users}
        />
      )}
    </>
  );
}

// ── Support Tab ──────────────────────────────────────────────────────────────

function SupportTab() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/v1/admin/support/messages");
      if (!res.ok) throw new Error("Failed to load support messages");
      const data = await res.json();
      setMessages(data.messages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleReply(msgId) {
    if (!reply.trim()) return;
    const confirmed = window.confirm(
      "Send this reply? A notification email will be sent to the user."
    );
    if (!confirmed) return;
    setReplying(true);
    try {
      const res = await apiFetch(`/api/v1/admin/support/messages/${msgId}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reply }),
      });
      if (!res.ok) throw new Error("Failed to send reply");
      setReply("");
      setSelectedId(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setReplying(false);
    }
  }

  if (loading) return <div className={s.state}>Loading support messages...</div>;
  if (error) return <div className={s.stateError}>{error}</div>;
  if (!messages.length) return <div className={s.state}>No support messages yet.</div>;

  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            <th>User</th>
            <th>Category</th>
            <th>Message</th>
            <th>Status</th>
            <th>Received</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {messages.map((msg) => (
            <tr key={msg.id}>
              <td className={s.emailCell}>{msg.user_email}</td>
              <td>
                <span className={`${s.roleBadge} ${s.roleUser}`}>{msg.category}</span>
              </td>
              <td className={s.topicCell}>
                <div className={s.topicSv} style={{ maxWidth: 300, whiteSpace: "pre-wrap" }}>
                  {msg.message.length > 100 ? msg.message.slice(0, 100) + "..." : msg.message}
                </div>
              </td>
              <td>
                <span className={`${s.planBadge} ${msg.admin_reply ? s.planPremium : s.planFree}`}>
                  {msg.admin_reply ? "Replied" : "Pending"}
                </span>
              </td>
              <td className={s.dateCell}>{timeAgo(msg.created_at)}</td>
              <td>
                {selectedId === msg.id ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 200 }}>
                    <textarea
                      className={s.fieldInput}
                      rows={3}
                      placeholder="Type your reply..."
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      style={{ resize: "vertical", fontSize: "0.82rem" }}
                    />
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        className={s.submitBtn}
                        disabled={replying || !reply.trim()}
                        onClick={() => handleReply(msg.id)}
                        style={{ fontSize: "0.78rem", padding: "5px 12px" }}
                      >
                        {replying ? "Sending..." : "Send"}
                      </button>
                      <button
                        className={s.cancelBtn}
                        onClick={() => { setSelectedId(null); setReply(""); }}
                        style={{ fontSize: "0.78rem", padding: "5px 12px" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : msg.admin_reply ? (
                  <span className={`${s.planBadge} ${s.planPremium}`} style={{ fontSize: "0.75rem" }}>Sent</span>
                ) : (
                  <button
                    className={s.createBtn}
                    onClick={() => { setSelectedId(msg.id); setReply(""); }}
                    style={{ fontSize: "0.78rem", padding: "5px 12px" }}
                  >
                    Reply
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Waiting List Tab ─────────────────────────────────────────────────────────

function WaitingListTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch("/api/v1/admin/subscriptions/waiting-list");
        if (!res.ok) throw new Error("Failed to load waiting list");
        const data = await res.json();
        setEntries(data.entries);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <div className={s.state}>Loading waiting list...</div>;
  if (error) return <div className={s.stateError}>{error}</div>;
  if (!entries.length) return <div className={s.state}>No one on the waiting list yet.</div>;

  return (
    <>
      <div className={s.usersToolbar}>
        <span className={s.usersCount}>
          {entries.length} user{entries.length === 1 ? "" : "s"} on the waiting list
        </span>
      </div>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>User</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td className={s.emailCell}>{entry.user_email}</td>
                <td className={s.dateCell}>{formatDate(entry.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Story Analytics Detail ────────────────────────────────────────────────────

function StoryAnalyticsDetail({ storyId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedModule, setExpandedModule] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await apiFetch(`/api/v1/admin/hub/stories/${storyId}`);
        if (!res.ok) throw new Error("Failed to load story details");
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [storyId]);

  if (loading) return <div className={s.state}>Loading story details…</div>;
  if (error) return <div className={s.stateError}>{error}</div>;
  if (!data) return null;

  return (
    <div className={s.detailWrap}>
      <button className={s.backBtn} onClick={onBack}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        All Stories
      </button>

      <div className={s.detailHeader}>
        <div>
          <div className={s.detailEmail}>{data.topic_sv || "—"}</div>
          <div className={s.detailMetaText}>{data.topic_en || "—"}</div>
        </div>
      </div>

      <div className={s.storiesList}>
        <div className={s.detailSectionLabel}>Modules</div>
        {MODULE_ORDER.map((def) => {
          const mod = data.modules.find((m) => m.module_id === def.id) || {
            started_users: [], completed_users: [], feedbacks: [],
          };
          const isExpanded = expandedModule === def.id;
          return (
            <div key={def.id} className={s.storyCard}>
              <div className={s.storyCardHeader}>
                <div className={s.storyTopic}>
                  <div className={s.storyTopicSv}>
                    <span className={s.moduleStep}>{def.step}.</span> {def.title}
                  </div>
                </div>
                <div className={s.saModuleStats}>
                  <span className={s.saStatItem} title="Started">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#856404" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    {mod.started_users.length}
                  </span>
                  <span className={s.saStatItem} title="Completed">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2a6e3e" strokeWidth="2.5" strokeLinecap="round"><path d="M5 13l4 4L19 7"/></svg>
                    {mod.completed_users.length}
                  </span>
                  <span className={s.saStatItem} title="Boring" style={{ color: "#908070" }}>
                    <FaceSvg type="boring" size={16} /> {mod.feedbacks.filter(f => f.feeling === 1).length}
                  </span>
                  <span className={s.saStatItem} title="Good" style={{ color: "#2a6e3e" }}>
                    <FaceSvg type="good" size={16} /> {mod.feedbacks.filter(f => f.feeling === 2).length}
                  </span>
                  <span className={s.saStatItem} title="Exciting" style={{ color: "#a07820" }}>
                    <FaceSvg type="excited" size={16} /> {mod.feedbacks.filter(f => f.feeling === 3).length}
                  </span>
                </div>
              </div>

              <div className={s.saActions}>
                <button
                  className={s.saToggleBtn}
                  onClick={() => setExpandedModule(isExpanded ? null : def.id)}
                >
                  {isExpanded ? "Hide Users" : "See Users"} ({mod.started_users.length})
                </button>
                {mod.feedbacks.length > 0 && (
                  <span className={s.saFeedbackCount}>{mod.feedbacks.length} feedback{mod.feedbacks.length !== 1 ? "s" : ""}</span>
                )}
              </div>

              {isExpanded && (
                <div className={s.modulesTableWrap}>
                  {mod.started_users.length > 0 && (
                    <>
                      <div className={s.modulesLabel}>Users who started</div>
                      <table className={s.modulesTable}>
                        <thead>
                          <tr>
                            <th>Email</th>
                            <th>Started</th>
                            <th>Completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mod.started_users.map((u) => (
                            <tr key={u.user_id}>
                              <td className={s.emailCell}>{u.email}</td>
                              <td className={s.moduleDate}>{formatDate(u.started_at)}</td>
                              <td className={s.moduleDate}>{formatDate(u.completed_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {mod.feedbacks.length > 0 && (
                    <>
                      <div className={s.modulesLabel} style={{ marginTop: 16 }}>Feedbacks</div>
                      <div className={s.saFeedbackList}>
                        {mod.feedbacks.map((fb, i) => {
                          const feelingInfo = fb.feeling != null ? FEELING_MAP[fb.feeling] : null;
                          return (
                            <div key={fb.user_id} className={s.saFeedbackEntry}>
                              <div className={s.saFeedbackHeader}>
                                <span className={s.emailCell}>{fb.email}</span>
                                {feelingInfo && (
                                  <span className={s.feelingWrap} style={{ color: feelingInfo.color }}>
                                    <FaceSvg type={feelingInfo.type} size={16} />
                                    <span className={s.feelingLabel}>{feelingInfo.label}</span>
                                  </span>
                                )}
                              </div>
                              <div className={s.saFeedbackText}>{fb.feedback}</div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {mod.started_users.length === 0 && (
                    <div className={s.detailEmpty}>No users have interacted with this module yet.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Stories Tab ───────────────────────────────────────────────────────────────

function StoriesTab() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedStoryId, setSelectedStoryId] = useState(null);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/v1/admin/hub/stories");
      if (!res.ok) throw new Error("Failed to load stories");
      const data = await res.json();
      setStories(data.stories);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (selectedStoryId) {
    return (
      <StoryAnalyticsDetail
        storyId={selectedStoryId}
        onBack={() => setSelectedStoryId(null)}
      />
    );
  }

  if (loading) return <div className={s.state}>Loading stories…</div>;
  if (error) return <div className={s.stateError}>{error}</div>;
  if (!stories.length) return <div className={s.state}>No stories yet.</div>;

  return (
    <>
      <div className={s.usersToolbar}>
        <span className={s.usersCount}>{stories.length} stories</span>
        <button className={s.createBtn} onClick={load} disabled={loading}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 4v6h-6M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Refresh
        </button>
      </div>

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Topic</th>
              <th>Started</th>
              <th>Completed</th>
              <th>Module Feelings</th>
            </tr>
          </thead>
          <tbody>
            {stories.map((story, idx) => {
              const totalFeelings = story.modules.reduce((sum, m) => sum + m.feeling_boring + m.feeling_good + m.feeling_exciting, 0);
              const totalBoring = story.modules.reduce((sum, m) => sum + m.feeling_boring, 0);
              const totalGood = story.modules.reduce((sum, m) => sum + m.feeling_good, 0);
              const totalExciting = story.modules.reduce((sum, m) => sum + m.feeling_exciting, 0);
              return (
                <tr key={story.story_id} className={s.clickableRow} onClick={() => setSelectedStoryId(story.story_id)}>
                  <td className={s.numCell}>
                    {story.on_admin_board ? (
                      <span className={s.saBoardBadge}>{story.board_position + 1}</span>
                    ) : (
                      <span className={s.moduleDash}>—</span>
                    )}
                  </td>
                  <td className={s.topicCell}>
                    <div className={s.topicSv}>{story.topic_sv || "—"}</div>
                    <div className={s.topicEn}>{story.topic_en || "—"}</div>
                  </td>
                  <td className={s.numCell}>{story.started_count}</td>
                  <td className={s.numCell}>{story.completed_count}</td>
                  <td>
                    {totalFeelings > 0 ? (
                      <div className={s.saFeelingsInline}>
                        <span style={{ color: "#908070" }}><FaceSvg type="boring" size={16} /> {totalBoring}</span>
                        <span style={{ color: "#2a6e3e" }}><FaceSvg type="good" size={16} /> {totalGood}</span>
                        <span style={{ color: "#a07820" }}><FaceSvg type="excited" size={16} /> {totalExciting}</span>
                      </div>
                    ) : (
                      <span className={s.moduleDash}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Root Panel ────────────────────────────────────────────────────────────────

const TABS = [
  { key: "monitor", label: "Monitor" },
  { key: "stories", label: "Stories" },
  { key: "users", label: "Users" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "waiting-list", label: "Waiting List" },
  { key: "support", label: "Support" },
];

export default function MonitorPanel() {
  const [activeTab, setActiveTab] = useState("monitor");

  return (
    <div className={s.panel}>
      <div className={s.header}>
        <h2 className={s.title}>Admin Hub</h2>
        <div className={s.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`${s.tab} ${activeTab === tab.key ? s.tabActive : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className={s.body}>
        {activeTab === "monitor" && <MonitorTab />}
        {activeTab === "stories" && <StoriesTab />}
        {activeTab === "users" && <UsersTab />}
        {activeTab === "subscriptions" && <SubscriptionsTab />}
        {activeTab === "waiting-list" && <WaitingListTab />}
        {activeTab === "support" && <SupportTab />}
      </div>
    </div>
  );
}
